/**
 * PAPPO — ChatWindow Component
 * Área de mensagens com cabeçalho do contacto e campo de entrada.
 * Combina MessageBubble + MessageInput. Suporta 1:1 e o grupo "Família".
 */

import { useRef, useEffect, useState, useMemo } from 'react';
import type { Message, Conversation } from '../../types/message';
import { Avatar } from '../common/Avatar';
import { ImageCropper } from '../common/ImageCropper';
import { MessageBubble } from './MessageBubble';
import { MessageInput } from './MessageInput';
import { ForwardModal } from './ForwardModal';
import { messagePreview } from './messagePreview';
import { ArrowLeft, Check, Image as ImageIcon, MoreVertical, Pencil, Users, X } from 'lucide-react';
import styles from './ChatWindow.module.css';

const ROLE_LABEL: Record<string, string> = {
  parent: 'Responsável',
  child: 'Criança',
  admin: 'Administrador',
};

interface ChatWindowProps {
  /** Conversa ativa */
  conversation: Conversation;
  /** Mensagens da conversa */
  messages: Message[];
  /** ID do utilizador atual */
  currentUserId: number;
  /** Callback ao enviar mensagem (opcionalmente citando outra) */
  onSendMessage: (content: string, replyToId?: number | null) => void;
  /** Callback ao enviar imagem (url + legenda opcional) */
  onSendImage?: (url: string, caption: string) => void;
  /** Callback ao enviar figurinha (url interna /uploads já rehospedada) */
  onSendSticker?: (url: string) => void;
  /** Callback ao enviar nota de voz (url interna /uploads) */
  onSendAudio?: (url: string) => void;
  /** Callback ao enviar vídeo (url interna /uploads + legenda opcional) */
  onSendVideo?: (url: string, caption: string) => void;
  /** Callback ao reagir a uma mensagem */
  onReact?: (messageId: number, emoji: string) => void;
  /** Callback para voltar (mobile) */
  onBack?: () => void;
  /** Presença em tempo real do outro participante (1:1) */
  isOnline?: boolean;
  /** Nome de quem está "a escrever..." (null = ninguém) */
  typingName?: string | null;
  /** Notifica o estado de digitação (debounce interno no input) */
  onTyping?: (isTyping: boolean) => void;
  /** Grupo família: se o utilizador atual pode editar nome/foto (adulto) */
  canEditGroup?: boolean;
  /** Grupo família: renomear */
  onRenameGroup?: (name: string) => void;
  /** Grupo família: trocar a foto (ficheiro de imagem) */
  onChangeGroupPhoto?: (file: File) => void;
  /** Todas as conversas (para o seletor de encaminhar) */
  conversations?: Conversation[];
  /** Encaminha uma mensagem para outra conversa */
  onForwardMessage?: (message: Message, targetConversationId: number) => void;
}

export function ChatWindow({
  conversation,
  messages,
  currentUserId,
  onSendMessage,
  onSendImage,
  onSendSticker,
  onSendAudio,
  onSendVideo,
  onReact,
  onBack,
  isOnline = false,
  typingName = null,
  onTyping,
  canEditGroup = false,
  onRenameGroup,
  onChangeGroupPhoto,
  conversations,
  onForwardMessage,
}: ChatWindowProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isGroup = !!conversation.is_group;
  const name = isGroup
    ? conversation.name || 'Família'
    : conversation.other_user?.display_name ?? 'Utilizador';
  const avatarSrc = isGroup
    ? conversation.avatar_url
    : conversation.other_user?.avatar_url;

  /* Nome do remetente por id (para rotular as bolhas em grupo) */
  const nameById = useMemo(() => {
    const m = new Map<number, string>();
    (conversation.participants ?? []).forEach((p) => m.set(p.id, p.display_name));
    return m;
  }, [conversation.participants]);

  /* Estado de edição inline do nome do grupo */
  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState(name);
  const [menuOpen, setMenuOpen] = useState(false);
  const [membersOpen, setMembersOpen] = useState(false);
  /* Foto escolhida à espera de recorte (abre o ImageCropper) */
  const [photoToCrop, setPhotoToCrop] = useState<File | null>(null);
  /* Responder (citar) e encaminhar */
  const [replyingTo, setReplyingTo] = useState<Message | null>(null);
  const [forwardingMsg, setForwardingMsg] = useState<Message | null>(null);
  useEffect(() => {
    setDraftName(name);
    setEditing(false);
    setMenuOpen(false);
    setMembersOpen(false);
    setPhotoToCrop(null);
    setReplyingTo(null);
    setForwardingMsg(null);
  }, [conversation.id, name]);

  const saveName = () => {
    const trimmed = draftName.trim();
    if (trimmed && trimmed !== name) onRenameGroup?.(trimmed);
    setEditing(false);
  };

  /* Auto-scroll ao receber nova mensagem ou quando o outro começa a escrever */
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, typingName]);

  const canEdit = isGroup && canEditGroup;
  const memberCount = conversation.participants?.length ?? 0;

  /* Contexto do banner de resposta (autor + preview curto da msg citada) */
  const replyContext = replyingTo
    ? {
        id: replyingTo.id,
        author:
          replyingTo.sender_id === currentUserId
            ? 'Você'
            : replyingTo.sender_name ||
              nameById.get(replyingTo.sender_id) ||
              conversation.other_user?.display_name ||
              'Contato',
        text: messagePreview(replyingTo),
      }
    : null;

  return (
    <div className={styles.window}>
      {/* Header */}
      <header className={styles.header}>
        {onBack && (
          <button className={styles.backBtn} onClick={onBack} aria-label="Voltar">
            <ArrowLeft size={20} />
          </button>
        )}
        <Avatar name={name} src={avatarSrc} size="sm" online={isGroup ? false : isOnline} />
        {canEdit && (
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) setPhotoToCrop(f); // abre o recorte antes de enviar
              e.target.value = '';
            }}
          />
        )}
        <div className={styles.headerInfo}>
          {editing ? (
            <div className={styles.renameRow}>
              <input
                className={styles.renameInput}
                value={draftName}
                autoFocus
                maxLength={120}
                onChange={(e) => setDraftName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveName();
                  if (e.key === 'Escape') setEditing(false);
                }}
              />
              <button onClick={saveName} aria-label="Guardar nome" className={styles.iconBtn}>
                <Check size={16} />
              </button>
              <button onClick={() => setEditing(false)} aria-label="Cancelar" className={styles.iconBtn}>
                <X size={16} />
              </button>
            </div>
          ) : (
            <span className={styles.headerName}>{name}</span>
          )}
          <span className={styles.headerStatus}>
            {isGroup
              ? typingName
                ? `${typingName} está a escrever...`
                : `${memberCount} ${memberCount === 1 ? 'membro' : 'membros'}`
              : typingName
                ? 'a escrever...'
                : isOnline
                  ? 'Online'
                  : 'Offline'}
          </span>
        </div>

        {isGroup && (
          <div className={styles.menuWrap}>
            <button
              className={styles.iconBtn}
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="Opções do grupo"
              aria-haspopup="menu"
            >
              <MoreVertical size={20} />
            </button>
            {menuOpen && (
              <>
                <div className={styles.menuBackdrop} onClick={() => setMenuOpen(false)} />
                <div className={styles.menu} role="menu">
                  <button
                    className={styles.menuItem}
                    role="menuitem"
                    onClick={() => {
                      setMembersOpen(true);
                      setMenuOpen(false);
                    }}
                  >
                    <Users size={16} /> Ver participantes
                  </button>
                  {canEdit && (
                    <button
                      className={styles.menuItem}
                      role="menuitem"
                      onClick={() => {
                        setDraftName(name);
                        setEditing(true);
                        setMenuOpen(false);
                      }}
                    >
                      <Pencil size={16} /> Renomear grupo
                    </button>
                  )}
                  {canEdit && (
                    <button
                      className={styles.menuItem}
                      role="menuitem"
                      onClick={() => {
                        fileInputRef.current?.click();
                        setMenuOpen(false);
                      }}
                    >
                      <ImageIcon size={16} /> Trocar foto
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </header>

      {isGroup && membersOpen && (
        <div className={styles.membersOverlay} onClick={() => setMembersOpen(false)}>
          <div className={styles.membersCard} onClick={(e) => e.stopPropagation()}>
            <div className={styles.membersHead}>
              <span className={styles.membersTitle}>Participantes · {memberCount}</span>
              <button
                className={styles.iconBtn}
                onClick={() => setMembersOpen(false)}
                aria-label="Fechar"
              >
                <X size={18} />
              </button>
            </div>
            <ul className={styles.memberList}>
              {(conversation.participants ?? []).map((p) => (
                <li key={p.id} className={styles.memberRow}>
                  <Avatar name={p.display_name} src={p.avatar_url} size="sm" />
                  <div className={styles.memberInfo}>
                    <span className={styles.memberName}>
                      {p.display_name}
                      {p.id === currentUserId ? ' (você)' : ''}
                    </span>
                    <span className={styles.memberRole}>{ROLE_LABEL[p.role] ?? p.role}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Recorte da foto do grupo — só adultos (canEdit) chegam aqui */}
      {photoToCrop && (
        <ImageCropper
          file={photoToCrop}
          onCancel={() => setPhotoToCrop(null)}
          onApply={(cropped) => {
            onChangeGroupPhoto?.(cropped);
            setPhotoToCrop(null);
          }}
        />
      )}

      {/* Messages */}
      <div className={styles.messages} role="list" aria-label="Mensagens">
        {messages.map((msg) => (
          <MessageBubble
            key={msg.id}
            message={msg}
            currentUserId={currentUserId}
            onReact={onReact}
            showSender={isGroup && msg.sender_id !== currentUserId}
            senderName={msg.sender_name || nameById.get(msg.sender_id)}
            onReply={setReplyingTo}
            onForward={onForwardMessage ? setForwardingMsg : undefined}
          />
        ))}
        {typingName && !isGroup && (
          <div className={styles.headerStatus} aria-live="polite">
            {`${typingName} está a escrever...`}
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <MessageInput
        onSend={onSendMessage}
        onSendImage={onSendImage}
        onSendSticker={onSendSticker}
        onSendAudio={onSendAudio}
        onSendVideo={onSendVideo}
        onTyping={onTyping}
        replyingTo={replyContext}
        onCancelReply={() => setReplyingTo(null)}
      />

      {forwardingMsg && conversations && onForwardMessage && (
        <ForwardModal
          conversations={conversations}
          currentConversationId={conversation.id}
          onClose={() => setForwardingMsg(null)}
          onPick={(targetId) => {
            onForwardMessage(forwardingMsg, targetId);
            setForwardingMsg(null);
          }}
        />
      )}
    </div>
  );
}
